import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "@/lib/api/client";
import { toast } from "@/components/ui/toast";
import { translateApiError } from "@/lib/errors/error-map";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface GeneralSettings {
  low_stock_threshold_default: number;
  order_void_window_minutes: number;
  warranty_defaults: Record<string, number>;
}

// Simple key-value editor for warranty_defaults (brand → months).
function WarrantyEditor({
  value,
  onChange,
}: {
  value: Record<string, number>;
  onChange: (v: Record<string, number>) => void;
}) {
  const [newBrand, setNewBrand] = useState("");
  const [newMonths, setNewMonths] = useState("");

  const entries = Object.entries(value);

  return (
    <div className="space-y-2">
      {entries.map(([brand, months]) => (
        <div key={brand} className="flex items-center gap-2">
          <Input value={brand} readOnly className="w-40 h-8 text-sm bg-muted" />
          <Input
            type="number"
            value={months}
            onChange={(e) => onChange({ ...value, [brand]: Number(e.target.value) })}
            className="w-20 h-8 text-sm"
            min={0}
          />
          <span className="text-xs text-muted-foreground">tháng</span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-destructive"
            onClick={() => {
              const copy = { ...value };
              delete copy[brand];
              onChange(copy);
            }}
          >
            Xoá
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <Input placeholder="Thương hiệu" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} className="w-40 h-8 text-sm" />
        <Input placeholder="Tháng" type="number" value={newMonths} onChange={(e) => setNewMonths(e.target.value)} className="w-20 h-8 text-sm" min={0} />
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          onClick={() => {
            if (!newBrand || !newMonths) return;
            onChange({ ...value, [newBrand]: Number(newMonths) });
            setNewBrand("");
            setNewMonths("");
          }}
        >
          Thêm
        </Button>
      </div>
    </div>
  );
}

export function GeneralSettingsPage() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<GeneralSettings>({
    queryKey: ["settings-general"],
    queryFn: () => apiGet("/v1/settings/general"),
  });

  const [form, setForm] = useState<GeneralSettings>({
    low_stock_threshold_default: 5,
    order_void_window_minutes: 60,
    warranty_defaults: {},
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const save = useMutation({
    mutationFn: () => apiPut("/v1/settings/general", form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings-general"] });
      toast.success("Đã lưu cài đặt chung");
    },
    onError: (e) => toast.error(translateApiError(e)),
  });

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      <h1 className="text-xl font-semibold">Cài đặt chung</h1>

      <div className="space-y-1">
        <Label>Ngưỡng cảnh báo tồn kho thấp (mặc định)</Label>
        <Input
          type="number"
          min={0}
          value={form.low_stock_threshold_default}
          onChange={(e) => setForm((f) => ({ ...f, low_stock_threshold_default: Number(e.target.value) }))}
        />
        <p className="text-xs text-muted-foreground">Áp dụng cho sản phẩm không có ngưỡng riêng.</p>
      </div>

      <div className="space-y-1">
        <Label>Thời gian cho phép huỷ đơn (phút)</Label>
        <Input
          type="number"
          min={0}
          value={form.order_void_window_minutes}
          onChange={(e) => setForm((f) => ({ ...f, order_void_window_minutes: Number(e.target.value) }))}
        />
        <p className="text-xs text-muted-foreground">Đơn đã thanh toán chỉ có thể huỷ trong khoảng thời gian này.</p>
      </div>

      <div className="space-y-2">
        <Label>Thời hạn bảo hành mặc định theo thương hiệu</Label>
        <WarrantyEditor
          value={form.warranty_defaults}
          onChange={(v) => setForm((f) => ({ ...f, warranty_defaults: v }))}
        />
      </div>

      <Button onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending ? "Đang lưu…" : "Lưu cài đặt"}
      </Button>
    </div>
  );
}
